import React, { useEffect } from 'react';
import { AlertProvider } from '@/context/alert-context/alert-provider';
import { DialogProvider } from '@/context/dialog-context/dialog-provider';
import { KeyboardShortcutsProvider } from '@/context/keyboard-shortcuts-context/keyboard-shortcuts-provider';
import { LocalConfigProvider } from '@/context/local-config-context/local-config-provider';
import { ThemeProvider } from '@/context/theme-context/theme-provider';
import { AuthProvider } from '@/context/auth-context/auth-provider';
import { ViewProvider } from '@/context/view-context/view-provider';
import { SocketProvider } from '@/context/socket-context/socket-provider';
import { UserStatusProvider } from '@/context/status-context/user-status-provider';
import { LayoutProvider } from '@/context/layout-context/layout-provider';
import { ModalProvider } from '@/components/providers/modal-provider';
import { ContextMenuProvider } from '@/components/providers/mock-context-menu-provider';
import { SettingsProvider } from '@/context/settings-context/settings-provider';
import { VolumeProvider } from '@/context/call-context/volume-provider';
import { CallProvider } from '@/context/call-context/call-provider.tsx';
import { Outlet, useLocation, useRevalidator } from 'react-router-dom';
import { FriendsProvider } from '@/context/firends-context/friends-provider.tsx';
import { NavigationSidebar } from '@/components/navigation/navigation-sidebar.tsx';
import { cn } from '@heroui/theme';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { GlobalAudioProvider } from './context/global-audio-context/global-audio-provider';
import { GlobalSFXProvider } from '@/context/sfx-context/sfx-provider.tsx';
import { ServiceWorkerController } from '@/components/service-worker-controller.tsx';
import { DirectMessagesProvider } from '@/context/conversations-context/direct-messages-provider.tsx';
import { VersionChecker } from '@/components/version-checker.tsx';
import { ExternalSocketProviderWithReconnect } from '@/context/socket-context/external-socket-provider-with-reconnect.tsx';
import { ScreenShareSettingsProvider } from '@/context/call-context/screen-share-settings-provider.tsx';
import { DeepLinkProvider } from '@/context/deep-link-context/deep-link-provider.tsx';
import { MaintenanceNotice } from '@/components/maintencance-notice.tsx';
import { CaptionProvider } from '@/context/caption-context/caption-provider.tsx';
import { ImportantUpdateRequiredBanner } from '@/components/important-update-required-banner.tsx';


export const RootLayout = () => {
  const location = useLocation();
  const { revalidate } = useRevalidator();
  const hideSidebar =
    location.pathname.startsWith('/auth') || location.pathname.startsWith('/invite'); // Routes where sidebar should NOT be shown

  useEffect(() => {
    if (location.state?.fromAuth) {
      revalidate();
    }
  }, [location, revalidate]);

  const preloadPages = () => {
    import('./pages/channels/@me/me-page');
    import('./pages/channels/@me/me-dm-page');
    import('./pages/channels/@me/friends-page');
    import('./pages/channels/@me/message-requests-page');
    import('./pages/channels/@me/account');
    import('./pages/channels/@me/change-password-page');
    import('./pages/channels/@me/devices-page');
    import('./pages/channels/@me/appearance-page');
    import('./pages/channels/@me/voice-video-page');
    import('./pages/channels/@me/notifications-page');
    import('./pages/channels/@me/language-page');
    // import('./pages/channels/@me/advanced-page');
    import('./pages/channels/@me/gamesdb-page');
    import('./pages/channels/@me/activity-privacy-page');
    import('./pages/channels/@me/boost/upgrade-page');
    // import('./pages/legal/privacy-page');
    // import('./pages/legal/terms-page');
    import('./pages/discovery/discovery-page');
  };

  useEffect(() => {
    preloadPages();
  }, []);

  return (
    <AuthProvider>
      <SettingsProvider>
        <LocalConfigProvider>
          <GlobalSFXProvider>
            <ThemeProvider>
              {/*<FullScreenLoaderProvider>*/}
              <LayoutProvider>
                {/*<PlatformGuard>*/}
                <ExternalSocketProviderWithReconnect>
                  <SocketProvider>
                    <UserStatusProvider>
                      <VolumeProvider>
                        <GlobalAudioProvider>
                          <ScreenShareSettingsProvider>
                            <CaptionProvider>
                              <CallProvider>
                                <ContextMenuProvider>
                                  <AlertProvider>
                                    <DialogProvider>
                                      <KeyboardShortcutsProvider>
                                        <ModalProvider>
                                          <ViewProvider>
                                            <FriendsProvider>
                                              <DirectMessagesProvider>
                                                <ServiceWorkerController />
                                                <DeepLinkProvider>
                                                  <div className="flex h-dvh w-dvw">
                                                    {!hideSidebar && (
                                                      <div className="fixed inset-y-0 z-30 hidden h-full w-[72px] flex-col md:flex">
                                                        <NavigationSidebar />
                                                      </div>
                                                    )}
                                                    <div
                                                      className={cn(
                                                        'flex-1 flex flex-col overflow-hidden bg-white dark:bg-[#292b30]',
                                                        !hideSidebar && 'md:pl-[72px]',
                                                        isPardusBrowser() && 'pt-[2em]'
                                                      )}
                                                    >
                                                      <div className="flex-1 overflow-auto">
                                                        {/*<PersistentSubscriptionBanner />*/}
                                                        <Outlet />
                                                      </div>
                                                    </div>
                                                    <VersionChecker />
                                                    <MaintenanceNotice />
                                                  </div>
                                                </DeepLinkProvider>
                                              </DirectMessagesProvider>
                                            </FriendsProvider>
                                          </ViewProvider>
                                        </ModalProvider>
                                      </KeyboardShortcutsProvider>
                                    </DialogProvider>
                                  </AlertProvider>
                                </ContextMenuProvider>
                              </CallProvider>
                            </CaptionProvider>
                          </ScreenShareSettingsProvider>
                        </GlobalAudioProvider>
                      </VolumeProvider>
                    </UserStatusProvider>
                  </SocketProvider>
                </ExternalSocketProviderWithReconnect>
                {/*</PlatformGuard>*/}
              </LayoutProvider>
              {/*</FullScreenLoaderProvider>*/}
            </ThemeProvider>
          </GlobalSFXProvider>
        </LocalConfigProvider>
      </SettingsProvider>
    </AuthProvider>
  );
};
